






























import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Doughnut } from 'vue-chartjs/legacy';
import { CreateSurveyElement } from '@/models/Presentation';
import { ChartData, ChartOptions } from 'chart.js';

@Component({
  components: { Doughnut }
})
export default class PieChart extends Vue {
  @Prop() surveyElement!: CreateSurveyElement;
  @Prop() values!: number[];
  @Prop() totalData!: number;
  @Prop({ default: false }) large!: boolean;

  containerSize = { width: 0, height: 0 };

  chartKey = 0;
  animationDuration = 1000;

  fontSize = 14;

  mounted() {
    const container = this.$refs.container as HTMLElement;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        this.containerSize.width = entry.contentRect.width;
        this.containerSize.height = entry.contentRect.height;
      }
    });

    resizeObserver.observe(container);
  }

  get filteredAnswers() {
    return this.surveyElement.answers.filter((_, i) => this.values[i] > 0);
  }

  get filteredValues() {
    return this.values.filter((_, i) => this.values[i] > 0);
  }

  get chartData(): ChartData {
    return {
      labels: this.filteredAnswers.map((answer, i) =>
        this.surveyElement.chartNumberType == 'PERCENT'
          ? `${(this.filteredValues[i] / this.totalData * 100).toFixed(1)}%`
          : this.filteredValues[i]),
      datasets: [
        {
          label: '',
          data: this.surveyElement.chartNumberType === 'PERCENT'
            ? this.filteredValues.map(value => ((value / this.totalData) * 100))
            : this.filteredValues,
          backgroundColor: this.filteredAnswers.map(answer => answer.color),
          borderRadius: this.large ? 10 : 5
        }
      ]
    };
  }

  @Watch('containerSize', { deep: true })
  onContainerSizeChange() {
    this.calculateFontSize();
  }

  @Watch('surveyElement.answers', { deep: true })
  onSurveyElementChange() {
    setTimeout(() => this.calculateFontSize(), 50);
  }

  async calculateFontSize() {
    await new Promise(
      resolve => setTimeout(resolve, 50)
    );
    console.log('Calculating font size');
    const legend = this.$refs.legend as HTMLElement;
    const spacePerItem = this.containerSize.height / this.surveyElement.answers.length;

    let fontFactor = 0.4;

    if (!legend) {
      console.log('Legend not found');
      this.fontSize = Math.min(Math.max(spacePerItem * fontFactor, 5), 16);
    }

    for (let i = 0; i < 4; i++) {
      if (legend.clientHeight * 0.9 > this.containerSize.height) {
        fontFactor *= 0.7;
      } else if (legend.clientHeight < this.containerSize.height * 0.6) {
        fontFactor *= 1.3;
      } else {
        break;
      }
      // Calculate the number of text lines based on the text length. Max 2 lines per answer
      const totalRows = this.surveyElement.answers.length;
      const availableSpace = legend.clientHeight;

      const spacePerRow = availableSpace / totalRows;
      console.log(`Available space: ${availableSpace}, Space per row: ${spacePerRow}, Total rows: ${totalRows}, Suggested Size: ${spacePerRow * 0.3}`);

      this.fontSize = Math.max(this.containerSize.height < 330 ? 8 : 10, Math.min(spacePerRow * fontFactor, 16));

      await new Promise(
        resolve => setTimeout(resolve, 50)
      );
    }
  }

  get chartOptions(): ChartOptions {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: this.animationDuration
      },
      plugins: {
        tooltip: {
          enabled: true,
          displayColors: false,
          filter: (tooltipItem) => {
            const index = tooltipItem.dataIndex;
            const text = this.surveyElement.answers[index].text;
            return !!text && text.trim().length > 0;
          },
          callbacks: {
            title: (tooltipItems) => {
              if (!tooltipItems[0]) {
                return '';
              }
              const index = tooltipItems[0].dataIndex;
              const fullText = this.filteredAnswers[index].text;

              // Wenn du automatische Zeilenumbrüche willst, splitte manuell nach Länge
              const words = fullText.split(' ');
              const lines = [];
              let line = '';

              const maxCharsPerLine = (this.$refs.chart as HTMLElement).clientWidth / 12;
              words.forEach(word => {
                if ((line + word).length > maxCharsPerLine) {
                  lines.push(line);
                  line = word + ' ';
                } else {
                  line += word + ' ';
                }
              });
              lines.push(line.trim());
              return lines; // Tooltip wird mehrzeilig
            },
            label: () => '' // Verstecke Wert
          },
          // Optional: Tooltip-Position direkt über Balken
          position: 'nearest',
          yAlign: 'bottom',
          xAlign: 'center',
          padding: 10,
          titleFont: {
            size: 12
          },
          bodyFont: {
            size: 0 // Kein Bodytext
          }
        },
        legend: {
          display: false
        },
        datalabels: {
          formatter: (value: number) =>
            this.surveyElement.chartNumberType === 'PERCENT'
              ? `${(value).toFixed(0)}%`
              : value,
          color: this.labelColors,
          clip: false,
          anchor: 'center',
          font: {
            size: this.surveyElement.answers.length > 5 ? 10 : Math.min(this.containerSize.height, this.containerSize.width * 0.4) > 150 ? 16 : 14
          }
        }
      }
    };
  }

  get labelColors(): string[] {
    return this.surveyElement.answers.map(answer => {
      const r = parseInt(answer.color.substring(1, 3), 16);
      const g = parseInt(answer.color.substring(3, 5), 16);
      const b = parseInt(answer.color.substring(5, 7), 16);
      const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      return luminance < 128 ? '#fff' : '#000';
    });
  }

  formattedNumber(index: number): string {
    return this.surveyElement.chartNumberType === 'PERCENT'
      ? `${(this.values[index] / this.totalData * 100).toFixed(1)}%`
      : this.values[index] + '';
  }
}
